/**
 * Realtime gateway WebSocket client.
 * Browser auth sends the ticket in the first auth frame. The URL does not carry it.
 * The gateway also accepts an Authorization header, and a query token outside production.
 * Protocol: welcome / subscribe / unsubscribe / heartbeat / auth_refresh
 *        → snapshot / delta / pong / auth_result / error.
 *
 * Dashboard / product screens obtain a short-lived lab HMAC via
 * POST /v1/realtime/ticket (OIDC or lab session), then connect.
 * Platform Lab can still pass an explicit lab token.
 */
import { getPlatformLabToken, isPlatformLabEnabled, platformPost } from './platformClient';

const DEFAULT_WS_URL = 'ws://127.0.0.1:9004/v1/ws';
const TICKET_REFRESH_MARGIN_MS = 60_000;
const HEARTBEAT_MS = 25_000;

export function getRealtimeWsUrl() {
  const raw = process.env.REACT_APP_REALTIME_WS_URL;
  const fromEnv = typeof raw === 'string' ? raw.trim() : '';
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.host) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/v1/ws`;
  }
  return DEFAULT_WS_URL;
}

/**
 * Connect URL for a caller that already has a ticket.
 * The ticket is not placed on the query string. Returns null when no token is available.
 */
export function buildRealtimeConnectUrl(accessToken) {
  const token = typeof accessToken === 'string' && accessToken.trim()
    ? accessToken.trim()
    : getPlatformLabToken();
  if (!token) return null;
  return getRealtimeWsUrl();
}

/**
 * Exchange the current API bearer for a short-lived WebSocket lab ticket.
 * @returns {Promise<{ access_token: string, expires_at?: number, expires_in?: number }>}
 */
export async function fetchRealtimeTicket() {
  const res = await platformPost('/v1/realtime/ticket', {});
  const body = res?.data || {};
  const accessToken = typeof body.access_token === 'string' ? body.access_token.trim() : '';
  if (!accessToken) {
    throw new Error('realtime ticket missing access_token');
  }
  return {
    access_token: accessToken,
    expires_at: typeof body.expires_at === 'number' ? body.expires_at : undefined,
    expires_in: typeof body.expires_in === 'number' ? body.expires_in : undefined,
  };
}

/**
 * Resolve a WS ticket: prefer /v1/realtime/ticket; fall back to lab env token.
 */
export async function resolveRealtimeAccessToken({ preferLabEnv = false } = {}) {
  if (preferLabEnv) {
    const lab = getPlatformLabToken();
    if (lab) return { access_token: lab };
  }
  try {
    return await fetchRealtimeTicket();
  } catch (err) {
    const lab = getPlatformLabToken();
    if (lab) return { access_token: lab };
    throw err;
  }
}

/**
 * Product/lab session: connect, subscribe tags, heartbeat, reconnect + auth_refresh.
 */
export function createRealtimeSession({
  onMessage,
  onOpen,
  onClose,
  onError,
  autoReconnect = false,
  reconnectDelayMs = 1500,
  heartbeatMs = HEARTBEAT_MS,
  WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null,
} = {}) {
  let ws = null;
  let requestSeq = 0;
  let intentionalClose = false;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let ticketRefreshTimer = null;
  let lastTags = null;
  let currentToken = null;
  let welcomed = false;
  const lastByType = {};

  function nextRequestId() {
    requestSeq += 1;
    return `rt-${requestSeq}`;
  }

  function clearReconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function clearHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function clearTicketRefresh() {
    if (ticketRefreshTimer) {
      clearTimeout(ticketRefreshTimer);
      ticketRefreshTimer = null;
    }
  }

  function scheduleTicketRefresh(expiresAtSec) {
    clearTicketRefresh();
    if (!expiresAtSec || !Number.isFinite(expiresAtSec)) return;
    const ms = expiresAtSec * 1000 - Date.now() - TICKET_REFRESH_MARGIN_MS;
    const delay = Math.max(5_000, ms);
    ticketRefreshTimer = setTimeout(async () => {
      try {
        const ticket = await fetchRealtimeTicket();
        currentToken = ticket.access_token;
        authRefresh(currentToken);
        scheduleTicketRefresh(ticket.expires_at);
      } catch (err) {
        if (onError) onError(err);
      }
    }, delay);
  }

  function startHeartbeat() {
    clearHeartbeat();
    if (!heartbeatMs || heartbeatMs <= 0) return;
    heartbeatTimer = setInterval(() => {
      heartbeat();
    }, heartbeatMs);
  }

  /**
   * @param {string} [accessToken] explicit ticket/lab token; otherwise lab env token
   */
  function connect(accessToken) {
    clearReconnect();
    intentionalClose = false;
    if (typeof accessToken === 'string' && accessToken.trim()) {
      currentToken = accessToken.trim();
    }
    const url = buildRealtimeConnectUrl(currentToken);
    if (!url) {
      const err = new Error(
        isPlatformLabEnabled()
          ? 'Realtime access token not available'
          : 'Platform lab token not configured',
      );
      if (onError) onError(err);
      return null;
    }
    if (!WebSocketImpl) {
      const err = new Error('WebSocket not available');
      if (onError) onError(err);
      return null;
    }
    // Close any prior socket before opening a new one (avoids stacked reconnects).
    if (ws) {
      try {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      } catch (_) { /* ignore */ }
      ws = null;
    }
    welcomed = false;
    ws = new WebSocketImpl(url);
    ws.onopen = () => {
      send('auth', { access_token: currentToken });
    };
    ws.onclose = (ev) => {
      clearHeartbeat();
      clearTicketRefresh();
      if (onClose) onClose(ev);
      if (!intentionalClose && autoReconnect) {
        reconnectTimer = setTimeout(() => {
          connect(currentToken);
        }, reconnectDelayMs);
      }
    };
    ws.onerror = (ev) => {
      if (onError) onError(ev);
    };
    ws.onmessage = (ev) => {
      let parsed = null;
      try {
        parsed = JSON.parse(ev.data);
      } catch (_) {
        parsed = { type: 'raw', payload: ev.data };
      }
      if (parsed?.type) lastByType[parsed.type] = parsed;
      if (parsed?.type === 'welcome' && !welcomed) {
        welcomed = true;
        startHeartbeat();
        if (onOpen) onOpen(ev);
        if (lastTags && lastTags.length) subscribe(lastTags);
      }
      if (onMessage) onMessage(parsed, ev);
    };
    return ws;
  }

  /**
   * Fetch a ticket (unless accessToken provided), then connect with reconnect.
   */
  async function connectWithTicket(accessToken, { expiresAt } = {}) {
    if (typeof accessToken === 'string' && accessToken.trim()) {
      currentToken = accessToken.trim();
      if (expiresAt) scheduleTicketRefresh(expiresAt);
      return connect(currentToken);
    }
    const ticket = await resolveRealtimeAccessToken();
    currentToken = ticket.access_token;
    scheduleTicketRefresh(ticket.expires_at);
    return connect(currentToken);
  }

  function send(type, payload = {}) {
    if (!ws || ws.readyState !== 1) return null;
    const envelope = {
      type,
      request_id: nextRequestId(),
      payload,
    };
    ws.send(JSON.stringify(envelope));
    return envelope.request_id;
  }

  function subscribe(tags) {
    lastTags = Array.isArray(tags) ? tags : null;
    return send('subscribe', { tags });
  }

  function unsubscribe(tags) {
    return send('unsubscribe', { tags });
  }

  function heartbeat() {
    return send('heartbeat', {});
  }

  function authRefresh(accessToken) {
    const token = typeof accessToken === 'string' && accessToken.trim()
      ? accessToken.trim()
      : currentToken || getPlatformLabToken();
    if (token) currentToken = token;
    return send('auth_refresh', { access_token: token });
  }

  function disconnect() {
    intentionalClose = true;
    clearReconnect();
    clearHeartbeat();
    clearTicketRefresh();
    if (ws) {
      ws.close();
      ws = null;
    }
  }

  function getLast(type) {
    return lastByType[type] || null;
  }

  function getAccessToken() {
    return currentToken;
  }

  return {
    connect,
    connectWithTicket,
    disconnect,
    subscribe,
    unsubscribe,
    heartbeat,
    authRefresh,
    send,
    getLast,
    getSocket: () => ws,
    getAccessToken,
  };
}
